import { useState, useCallback } from 'react';
import { Calendar, Clock, Users, Loader2, Shield, Receipt, Info, Tag, X, Check, AlertCircle, ChevronLeft, ChevronRight, Sparkles, CreditCard } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';
import { usePropertyContext } from '@/context/PropertyContext';
import { api, type ExperienceDetail } from '@/lib/api';
import type { BookingData, BookingPricing } from './BookingFlow';

interface SummaryStepProps {
  experience: ExperienceDetail;
  bookingData: BookingData;
  pricing: BookingPricing;
  onUpdate: (data: Partial<BookingData>) => void;
  onConfirm: () => void;
  onBack: () => void;
  isLoading: boolean;
  appliedDiscount?: {
    code: string;
    discountPercent: number;
    discountAmount: number;
  } | null;
  onApplyDiscount?: (code: string, discountPercent: number, discountAmount: number) => void;
  onRemoveDiscount?: () => void;
}

interface PromoCodeState {
  code: string;
  isValidating: boolean;
  isValid: boolean | null;
  discountPercent: number | null;
  discountAmount: number | null;
  error: string | null;
}

/**
 * SummaryStep - Premium receipt-style booking summary
 * Features elegant price breakdown and trust indicators
 */
export function SummaryStep({ 
  experience, 
  bookingData,
  pricing,
  onUpdate,
  onConfirm, 
  onBack, 
  isLoading,
  appliedDiscount,
  onApplyDiscount,
  onRemoveDiscount,
}: SummaryStepProps) {
  const { propertyKey } = usePropertyContext();
  
  const [promoState, setPromoState] = useState<PromoCodeState>({
    code: appliedDiscount?.code || bookingData.discountCode || '',
    isValidating: false,
    isValid: appliedDiscount ? true : (bookingData.discountCode ? true : null),
    discountPercent: appliedDiscount?.discountPercent || null,
    discountAmount: appliedDiscount?.discountAmount || null,
    error: null,
  });
  const [showPromoInput, setShowPromoInput] = useState(!!appliedDiscount || !!bookingData.discountCode);

  const formattedDate = new Date(bookingData.date).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedTime = formatTime(bookingData.time);
  
  const showServiceFee = pricing.feeDisplayMode === 'guest_pays' && 
    pricing.serviceFee !== null && 
    pricing.serviceFee > 0;

  const calculatedDiscount = promoState.isValid && promoState.discountPercent
    ? Math.round(pricing.subtotal * (promoState.discountPercent / 100))
    : promoState.isValid && promoState.discountAmount
    ? promoState.discountAmount
    : 0;

  const adjustedTotal = pricing.totalAmount - calculatedDiscount;

  const validatePromoCode = useCallback(async () => {
    if (!propertyKey || !promoState.code.trim()) {
      setPromoState(prev => ({ ...prev, error: 'Please enter a promo code' }));
      return;
    }

    setPromoState(prev => ({ ...prev, isValidating: true, error: null }));

    try {
      const result = await api.validateDiscountCode(
        propertyKey,
        promoState.code.trim().toUpperCase(),
        experience.id
      );

      if (result.valid) {
        setPromoState(prev => ({
          ...prev,
          isValidating: false,
          isValid: true,
          discountPercent: result.discountPercent || null,
          discountAmount: result.discountAmount || null,
          error: null,
        }));
        onUpdate({ discountCode: promoState.code.trim().toUpperCase() });
        if (onApplyDiscount) {
          onApplyDiscount(
            promoState.code.trim().toUpperCase(),
            result.discountPercent || 0,
            result.discountAmount || 0
          );
        }
      } else {
        setPromoState(prev => ({
          ...prev,
          isValidating: false,
          isValid: false,
          error: 'Invalid or expired promo code',
        }));
      }
    } catch (err) {
      setPromoState(prev => ({
        ...prev,
        isValidating: false,
        isValid: false,
        error: err instanceof Error ? err.message : 'Failed to validate code',
      }));
    }
  }, [propertyKey, promoState.code, experience.id, onUpdate, onApplyDiscount]);

  const removePromoCode = useCallback(() => {
    setPromoState({
      code: '',
      isValidating: false,
      isValid: null,
      discountPercent: null,
      discountAmount: null,
      error: null,
    });
    onUpdate({ discountCode: '' });
    setShowPromoInput(false);
    if (onRemoveDiscount) {
      onRemoveDiscount();
    }
  }, [onUpdate, onRemoveDiscount]);

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Receipt className="h-5 w-5 text-primary" />
          </div>
          <h2 className="font-serif text-xl font-medium text-foreground">Review Your Booking</h2>
        </div>
        <p className="text-sm text-muted-foreground ml-13">
          Please confirm all details before proceeding to payment
        </p>
      </div>

      {/* Experience Summary Card - Premium design */}
      <div className="rounded-2xl border border-border/50 bg-card shadow-soft overflow-hidden">
        {/* Header with Image */}
        <div className="flex gap-4 p-5 bg-gradient-to-r from-muted/50 to-muted/20 border-b border-border/30">
          {experience.coverImage && (
            <img
              src={experience.coverImage}
              alt={experience.name}
              className="h-20 w-20 rounded-xl object-cover shadow-sm shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-lg font-medium text-foreground truncate">{experience.name}</h3>
            {experience.shortDescription && (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                {experience.shortDescription}
              </p>
            )}
          </div>
        </div>

        {/* Booking Details - Refined layout */}
        <div className="p-5 space-y-4">
          <DetailRow icon={Calendar} label="Date" value={formattedDate} />
          <DetailRow icon={Clock} label="Time" value={formattedTime} />
          <DetailRow 
            icon={Users} 
            label="Guests" 
            value={`${pricing.totalGuests} ${pricing.totalGuests === 1 ? 'guest' : 'guests'}`} 
          />
        </div>

        {/* Guest Info - Premium card */}
        <div className="p-5 border-t border-border/30 bg-muted/10">
          <h4 className="text-xs font-semibold uppercase tracking-elegant text-muted-foreground mb-3">Contact Information</h4>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {bookingData.guestInfo.firstName} {bookingData.guestInfo.lastName}
            </p>
            <p className="text-sm text-muted-foreground">{bookingData.guestInfo.email}</p>
            <p className="text-sm text-muted-foreground">{bookingData.guestInfo.phone}</p>
          </div>
        </div>
      </div>

      {/* Promo Code Section - Elegant design */}
      <div className="mt-6">
        {!showPromoInput && !promoState.isValid ? (
          <button
            type="button"
            onClick={() => setShowPromoInput(true)}
            className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Tag className="h-4 w-4" />
            Have a promo code?
          </button>
        ) : promoState.isValid ? (
          <div className="flex items-center justify-between rounded-xl border border-success/30 bg-gradient-to-r from-success/5 to-success/10 p-4 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-success/15">
                <Check className="h-5 w-5 text-success" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-success-foreground">
                  Promo code applied: <span className="font-mono">{promoState.code}</span>
                </p>
                <p className="text-xs text-success">
                  {promoState.discountPercent
                    ? `${promoState.discountPercent}% off your booking`
                    : formatPrice(promoState.discountAmount || 0, experience.currency) + ' off your booking'
                  }
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={removePromoCode}
              className="rounded-lg p-2 text-success hover:bg-success/10 transition-colors"
              aria-label="Remove promo code"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Promo Code</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Tag className="h-4 w-4 text-muted-foreground/60" />
                </div>
                <input
                  type="text"
                  value={promoState.code}
                  onChange={(e) => setPromoState(prev => ({ 
                    ...prev, 
                    code: e.target.value.toUpperCase(),
                    error: null,
                    isValid: null 
                  }))}
                  placeholder="Enter code"
                  className={cn(
                    'input-luxury pl-11 font-mono uppercase',
                    promoState.error && 'border-destructive'
                  )}
                  disabled={promoState.isValidating}
                />
              </div>
              <button
                type="button"
                onClick={validatePromoCode}
                disabled={promoState.isValidating || !promoState.code.trim()}
                className="btn-luxury-secondary px-5"
              >
                {promoState.isValidating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Apply'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPromoInput(false);
                  setPromoState(prev => ({ ...prev, code: '', error: null }));
                }}
                className="btn-luxury-ghost px-3"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {promoState.error && (
              <div className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {promoState.error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pricing Breakdown - Premium receipt style */}
      <div className="mt-6 rounded-2xl border border-border/50 bg-card shadow-soft overflow-hidden">
        <div className="p-5 border-b border-border/30 bg-muted/20">
          <h4 className="font-serif text-lg font-medium text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Price Details
          </h4>
        </div>
        
        <div className="p-5 space-y-3">
          {/* Base Price */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">
              {experience.priceType === 'per_group' 
                ? 'Group Rate'
                : `${bookingData.adultCount} × ${formatPrice(experience.basePrice, experience.currency)}`
              }
            </span>
            <span className="font-medium">{formatPrice(pricing.baseAmount, experience.currency)}</span>
          </div>

          {/* Add-ons */}
          {bookingData.addOns.length > 0 && (
            <div className="pt-3 border-t border-border/30">
              <p className="text-xs font-semibold uppercase tracking-elegant text-muted-foreground mb-2">Add-ons</p>
              {bookingData.addOns.map((addOn) => {
                const addOnTotal = addOn.priceType === 'per_person'
                  ? addOn.price * addOn.quantity * pricing.totalGuests
                  : addOn.price * addOn.quantity;
                return (
                  <div key={addOn.id} className="flex justify-between items-center text-sm py-1">
                    <span className="text-muted-foreground">
                      {addOn.name} × {addOn.quantity}
                      {addOn.priceType === 'per_person' && ` × ${pricing.totalGuests}`}
                    </span>
                    <span className="font-medium">{formatPrice(addOnTotal, experience.currency)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Subtotal */}
          {(showServiceFee || calculatedDiscount > 0) && (
            <div className="flex justify-between items-center pt-3 border-t border-border/30">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatPrice(pricing.subtotal, experience.currency)}</span>
            </div>
          )}

          {/* Discount */}
          {calculatedDiscount > 0 && (
            <div className="flex justify-between items-center text-success">
              <span className="flex items-center gap-1.5">
                <Tag className="h-4 w-4" />
                Promo discount
                {promoState.discountPercent && ` (${promoState.discountPercent}%)`}
              </span>
              <span className="font-semibold">-{formatPrice(calculatedDiscount, experience.currency)}</span>
            </div>
          )}

          {/* Platform Fee */}
          {showServiceFee && (
            <div className="flex justify-between items-center text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CreditCard className="h-4 w-4" />
                Service fee ({Math.round((pricing.serviceFeeRate || 0) * 100)}%)
                <span className="group relative cursor-help">
                  <Info className="h-3.5 w-3.5" />
                  <span className="invisible absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-lg bg-foreground px-3 py-2 text-xs text-background opacity-0 transition-all group-hover:visible group-hover:opacity-100 whitespace-nowrap z-10 shadow-lg">
                    This fee helps us operate the booking platform
                  </span>
                </span>
              </span>
              <span className="font-medium">{formatPrice(pricing.serviceFee || 0, experience.currency)}</span>
            </div>
          )}
        </div>

        {/* Total - Premium styling */}
        <div className="p-5 bg-gradient-to-r from-primary/5 to-primary/10 border-t border-border/30">
          <div className="flex justify-between items-center">
            <span className="font-serif text-lg font-medium">Total</span>
            <div className="text-right">
              <span className={cn(
                'price-display-lg',
                calculatedDiscount > 0 && 'text-success'
              )}>
                {formatPrice(adjustedTotal, experience.currency)}
              </span>
              {calculatedDiscount > 0 && (
                <p className="text-sm text-muted-foreground line-through">
                  {formatPrice(pricing.totalAmount, experience.currency)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators - Premium design */}
      <div className="mt-6 rounded-xl bg-muted/30 border border-border/30 p-4">
        <div className="flex items-center gap-3">
          <div className="trust-badge-icon">
            <Shield className="h-4 w-4" strokeWidth={2} />
          </div>
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Secure payment</span> • Free cancellation up to 24 hours before
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="btn-luxury-ghost"
        >
          <ChevronLeft className="h-5 w-5" />
          Back
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="btn-luxury"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Continue to Payment
              <ChevronRight className="h-5 w-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function DetailRow({ 
  icon: Icon, 
  label, 
  value 
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  value: string;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex justify-between flex-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground">{value}</span>
      </div>
    </div>
  );
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}
